import { cn } from 'cn';

import { showTitleOnlyWhenTruncated } from '@vestigas/utils';

import { Icon, IconButton } from '@vestigas/ui';

import {
  type FilterBadge,
  type FilterBadgeState,
} from './savedFiltersBadgeRowUtils';

type SavedFilterBadgeProps = {
  readonly badge: FilterBadge;
  readonly editAriaLabel: string;
  readonly state: FilterBadgeState;
  readonly unsavedChangesLabel: string;
  readonly onEdit: (filter: NonNullable<FilterBadge['savedFilter']>) => void;
  readonly onSelect: (badge: FilterBadge) => void;
};

export function SavedFilterBadge({
  badge,
  editAriaLabel,
  state,
  unsavedChangesLabel,
  onEdit,
  onSelect,
}: SavedFilterBadgeProps) {
  return (
    <div
      className={cn(
        // max-w bounds the badge so the `truncate` below actually engages:
        // names go up to SAVED_FILTER_NAME_MAX_LENGTH chars, and an unbounded
        // badge would push the overflow and add buttons out of the clipped row.
        // The selected filter is pinned as the last visible tab, so it gets a
        // wider bound — it is the one name the user most needs to read.
        'inline-flex h-9 shrink-0 items-center gap-1 border-b-2 transition-colors',
        {
          'max-w-96 border-tiga-action-default text-tiga-action-default':
            state.isSelected,
          'max-w-64 border-transparent text-tiga-text-default hover:text-tiga-action-default':
            !state.isSelected,
        },
      )}
    >
      <button
        className={cn(
          'inline-flex h-full min-w-0 items-center gap-1 bg-transparent pl-4 text-sm transition-colors',
          {
            'cursor-pointer': !state.isSelected,
            'font-medium': state.isSelected,
          },
          state.isPersistedSavedFilter ? 'pr-0' : 'pr-4',
        )}
        type="button"
        onClick={() => {
          onSelect(badge);
        }}
      >
        <span
          // min-w-0 lets this flex item shrink past its content width, which
          // is what actually engages the truncation.
          className="min-w-0 truncate"
          onMouseEnter={(event) => {
            showTitleOnlyWhenTruncated(event, badge.label);
          }}
        >
          {badge.label}
        </span>
        {state.countLabel !== null && (
          <span
            aria-hidden
            className="shrink-0 rounded-full bg-blue-100 px-1.5 py-px text-xs font-medium text-tiga-action-default tabular-nums"
          >
            {state.countLabel}
          </span>
        )}
        {state.showDirty && (
          <span
            aria-label={unsavedChangesLabel}
            className="shrink-0 text-xs leading-none"
          >
            *
          </span>
        )}
      </button>
      {state.showEditPencil && badge.savedFilter ? (
        <IconButton
          aria-label={editAriaLabel}
          className="mr-2 text-tiga-action-default hover:text-tiga-action-default"
          size="sm"
          onClick={(event) => {
            event.preventDefault();
            event.stopPropagation();
            onEdit(badge.savedFilter);
          }}
        >
          <Icon metaphor="editOutlined" className="size-4.5" />
        </IconButton>
      ) : state.isPersistedSavedFilter ? (
        <span aria-hidden className="size-7 shrink-0" />
      ) : null}
    </div>
  );
}
